import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

export function ContactPage() {
  const { t, language } = useLanguage();

  return (
    <div data-testid="contact-page">
      <div className="bg-[#F8FAFC] py-16 md:py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Outfit']">
            {t('footer.contact')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Une question ? Un projet ? Contactez notre équipe commerciale.'
              : 'Have a question? A project? Contact our sales team.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="space-y-12">
            <div>
              <h2 className="text-2xl font-bold mb-8 font-['Outfit']">
                {language === 'fr' ? 'Nos coordonnées' : 'Contact Information'}
              </h2>
              
              <div className="space-y-6">
                <a
                  href="mailto:info@logotag.com"
                  className="flex items-start gap-4 p-4 border border-border hover:border-[#DC2626] transition-colors group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <p className="text-muted-foreground group-hover:text-[#DC2626] transition-colors">
                      info@logotag.com
                    </p>
                  </div>
                </a>

                <a
                  href="tel:+33491481213"
                  className="flex items-start gap-4 p-4 border border-border hover:border-[#DC2626] transition-colors group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">{language === 'fr' ? 'Téléphone' : 'Phone'}</p>
                    <p className="text-muted-foreground group-hover:text-[#DC2626] transition-colors">
                      +33 4 91 48 12 13
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 border border-border">
                  <div className="w-12 h-12 bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">{language === 'fr' ? 'Horaires' : 'Business Hours'}</p>
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Lun - Ven : 9h - 18h' : 'Mon - Fri: 9am - 6pm'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">
                {language === 'fr' ? 'Besoin d\'un devis rapidement ?' : 'Need a quote quickly?'}
              </h3>
              <Link to="/devis">
                <Button className="rounded-none bg-[#1F2937] hover:bg-[#1F2937]/90 uppercase tracking-widest text-xs px-8 py-5 h-auto">
                  {t('hero.cta.quote')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="border border-border p-8">
            <h2 className="text-xl font-bold mb-6 font-['Outfit']">
              {language === 'fr' ? 'Envoyez-nous un message' : 'Send us a message'}
            </h2>
            <form className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name">{t('quote.name')} *</Label>
                  <Input id="contact-name" required className="rounded-none mt-1" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="contact-company">{t('quote.company')}</Label>
                  <Input id="contact-company" className="rounded-none mt-1" data-testid="contact-company-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-email">{t('quote.email')} *</Label>
                <Input id="contact-email" type="email" required className="rounded-none mt-1" data-testid="contact-email-input" />
              </div>
              <div>
                <Label htmlFor="contact-phone">{t('quote.phone')}</Label>
                <Input id="contact-phone" type="tel" className="rounded-none mt-1" data-testid="contact-phone-input" />
              </div>
              <div>
                <Label htmlFor="contact-message">{t('quote.message')} *</Label>
                <Textarea id="contact-message" rows={5} required className="rounded-none mt-1 resize-none" data-testid="contact-message-input" />
              </div>
              <Button type="submit" className="w-full rounded-none bg-[#1F2937] hover:bg-[#1F2937]/90 uppercase tracking-widest text-xs py-5 h-auto" data-testid="contact-submit-btn">
                <Send className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Envoyer' : 'Send'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LegalPage({ type }) {
  const { language } = useLanguage();

  const content = {
    'mentions-legales': {
      title: { fr: 'Mentions légales', en: 'Legal Notice' },
      content: {
        fr: `
          <h2>Éditeur du site</h2>
          <p>Logotag<br/>
          Email : info@logotag.com<br/>
          Téléphone : +33 4 91 48 12 13</p>
          
          <h2>Hébergement</h2>
          <p>Le site est hébergé par des services cloud professionnels.</p>
          
          <h2>Propriété intellectuelle</h2>
          <p>L'ensemble des éléments figurant sur le site (textes, images, logos, etc.) sont protégés par le droit de la propriété intellectuelle. Toute reproduction est interdite sans autorisation préalable.</p>
        `,
        en: `
          <h2>Site Publisher</h2>
          <p>Logotag<br/>
          Email: info@logotag.com<br/>
          Phone: +33 4 91 48 12 13</p>
          
          <h2>Hosting</h2>
          <p>The site is hosted by professional cloud services.</p>
          
          <h2>Intellectual Property</h2>
          <p>All elements on the site (texts, images, logos, etc.) are protected by intellectual property law. Any reproduction is prohibited without prior authorization.</p>
        `
      }
    },
    'cgv': {
      title: { fr: 'Conditions Générales de Vente B2B', en: 'B2B Terms and Conditions' },
      content: {
        fr: `
          <h2>Article 1 - Objet</h2>
          <p>Les présentes conditions générales de vente s'appliquent à toutes les commandes passées auprès de Logotag par des clients professionnels.</p>
          
          <h2>Article 2 - Devis et commandes</h2>
          <p>Les devis sont valables 30 jours. Toute commande est ferme et définitive dès acceptation du devis par le client.</p>
          
          <h2>Article 3 - Personnalisation</h2>
          <p>Les fichiers de personnalisation doivent être fournis dans les formats spécifiés. Logotag ne pourra être tenu responsable d'un rendu ne correspondant pas aux attentes si les fichiers fournis ne sont pas conformes.</p>
          
          <h2>Article 4 - Délais de livraison</h2>
          <p>Les délais de livraison sont donnés à titre indicatif et peuvent varier selon la complexité des personnalisations demandées.</p>
        `,
        en: `
          <h2>Article 1 - Purpose</h2>
          <p>These general terms and conditions of sale apply to all orders placed with Logotag by professional customers.</p>
          
          <h2>Article 2 - Quotes and Orders</h2>
          <p>Quotes are valid for 30 days. Any order is firm and final upon acceptance of the quote by the customer.</p>
          
          <h2>Article 3 - Customization</h2>
          <p>Customization files must be provided in the specified formats. Logotag cannot be held responsible for results not meeting expectations if the files provided are not compliant.</p>
          
          <h2>Article 4 - Delivery Times</h2>
          <p>Delivery times are indicative and may vary depending on the complexity of requested customizations.</p>
        `
      }
    },
    'confidentialite': {
      title: { fr: 'Politique de confidentialité', en: 'Privacy Policy' },
      content: {
        fr: `
          <h2>Collecte des données</h2>
          <p>Nous collectons les données personnelles que vous nous fournissez lors de vos demandes de devis et la création de compte : nom, email, téléphone, entreprise.</p>
          
          <h2>Utilisation des données</h2>
          <p>Vos données sont utilisées pour traiter vos demandes, vous contacter et améliorer nos services.</p>
          
          <h2>Protection des données</h2>
          <p>Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données personnelles.</p>
          
          <h2>Vos droits</h2>
          <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Contactez-nous à info@logotag.com.</p>
        `,
        en: `
          <h2>Data Collection</h2>
          <p>We collect personal data you provide when requesting quotes and creating accounts: name, email, phone, company.</p>
          
          <h2>Data Use</h2>
          <p>Your data is used to process your requests, contact you, and improve our services.</p>
          
          <h2>Data Protection</h2>
          <p>We implement appropriate security measures to protect your personal data.</p>
          
          <h2>Your Rights</h2>
          <p>Under GDPR, you have the right to access, rectify, and delete your data. Contact us at info@logotag.com.</p>
        `
      }
    },
    'cookies': {
      title: { fr: 'Politique cookies', en: 'Cookie Policy' },
      content: {
        fr: `
          <h2>Qu'est-ce qu'un cookie ?</h2>
          <p>Un cookie est un petit fichier texte stocké sur votre navigateur lors de votre visite sur un site web.</p>
          
          <h2>Cookies utilisés</h2>
          <p>Nous utilisons des cookies essentiels pour le fonctionnement du site et des cookies d'analyse pour améliorer votre expérience.</p>
          
          <h2>Gestion des cookies</h2>
          <p>Vous pouvez configurer votre navigateur pour refuser les cookies. Cela peut toutefois affecter certaines fonctionnalités du site.</p>
        `,
        en: `
          <h2>What is a Cookie?</h2>
          <p>A cookie is a small text file stored on your browser when you visit a website.</p>
          
          <h2>Cookies Used</h2>
          <p>We use essential cookies for site functionality and analytics cookies to improve your experience.</p>
          
          <h2>Cookie Management</h2>
          <p>You can configure your browser to refuse cookies. However, this may affect some site features.</p>
        `
      }
    }
  };

  const page = content[type];
  if (!page) return null;

  return (
    <div data-testid={`legal-page-${type}`}>
      <div className="bg-[#F8FAFC] py-16 border-b border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">
            {page.title[language]}
          </h1>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <div 
          className="prose prose-slate max-w-none
            [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-['Outfit']
            [&_p]:text-muted-foreground [&_p]:mb-4"
          dangerouslySetInnerHTML={{ __html: page.content[language] }}
        />
      </div>
    </div>
  );
}

export default ContactPage;
